customElements.get("sticky-atc") ||
  customElements.define(
    "sticky-atc",
    class extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          prodTitle: ".m-sticky-addtocart--title",
          mainImage: ".m-sticky-addtocart--image",
          addToCart: ".m-add-to-cart",
          buyNowBtn: ".m-product-dynamic-checkout",
          variantIdSelect: '[name="id"]',
          foxkitBtn: ".foxkit-button",
        };
        this.hasCustomFields = !!document.querySelector(
          ".m-main-product--info .m-product-custom-field",
        );
      }

      connectedCallback() {
        this.container = this.closest(".m-sticky-addtocart");
        this.mainProductForm = document.querySelector(".m-product-form--main");
        this.mainProductInfo = document.querySelector(".m-main-product--info");
        this.mainAddToCart = this.mainProductForm?.querySelector(
          ".m-add-to-cart",
        );
        this.mainDynamicCheckout = this.mainProductForm?.querySelector(
          this.selectors.buyNowBtn,
        );
        this.productId = this.dataset.productId;
        this.selectedVariantDefault =
          this.dataset.disableSelectedVariantDefault === "true";
        this.domNodes = queryDomNodes(this.selectors, this.container);
        this.variantData = this.getVariantData();
        this.variantSelect = this.querySelector(this.selectors.variantIdSelect);

        this.prepareVariantSelect();
        this.init();
        this.updateFromSelection();
        this.observeStickyButton();
        this.setStickyAddToCartHeight();

        this.addEventListener("change", (event) => {
          if (
            event.target === this.variantSelect ||
            event.target?.matches?.(this.selectors.variantIdSelect)
          ) {
            this.updateFromSelection();
          }
        });

        this.querySelector(".sticky-atc-form")?.addEventListener(
          "submit",
          (event) => {
            if (!this.isValidVariant(this.currentVariant)) {
              event.preventDefault();
              event.stopPropagation();
              this.focusVariantSelect();
            }
          },
        );

        document.addEventListener("matchMobile", () => {
          this.setStickyAddToCartHeight();
        });
        document.addEventListener("unmatchMobile", () => {
          this.setStickyAddToCartHeight();
        });

        this.resizeObserver = new ResizeObserver(() => {
          this.setStickyAddToCartHeight();
        });
        this.resizeObserver.observe(this);
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.observer?.disconnect();
      }

      getVariantData() {
        if (this.variantData) return this.variantData;
        const dataNode = this.container?.querySelector(
          '[type="application/json"]',
        );

        try {
          return dataNode ? JSON.parse(dataNode.textContent) : [];
        } catch (error) {
          console.warn("Sticky cart variant data could not be parsed", error);
          return [];
        }
      }

      isPlaceholderValue(value) {
        return /^(please\s*(choose|select)(\s+.*)?|select\s+(an?\s+)?(option|options|model|vehicle|variant)(\s+.*)?|choose\s+(an?\s+)?(option|options|model|vehicle|variant)(\s+.*)?|not\s+selected|none\s+selected)$/i.test(
          String(value || "").trim(),
        );
      }

      isPlaceholderVariant(variant) {
        return !!variant?.options?.some((value) =>
          this.isPlaceholderValue(value),
        );
      }

      isValidVariant(variant) {
        return !!variant && !this.isPlaceholderVariant(variant);
      }

      prepareVariantSelect() {
        if (!this.variantSelect) {
          this.currentVariant = this.variantData[0] || null;
          return;
        }

        const formId =
          this.querySelector(".sticky-atc-form")?.getAttribute("id") ||
          `sticky-atc-form-${this.productId}`;
        const selectId = `${formId}-variant`;
        const label = this.querySelector('label[for="id"]');

        this.variantSelect.id = selectId;
        this.variantSelect.setAttribute("aria-label", "Select product options");
        this.variantSelect.required = true;

        if (label) {
          label.htmlFor = selectId;
        }

        Array.from(this.variantSelect.options).forEach((option) => {
          const variant = this.variantData.find(
            (item) => item.id === Number(option.value),
          );
          if (this.isPlaceholderVariant(variant)) {
            option.disabled = true;
            option.dataset.placeholderVariant = "true";
          }
        });

        const validOptions = Array.from(this.variantSelect.options).filter(
          (option) => option.value && !option.disabled,
        );

        if (validOptions.length === 1) {
          this.variantSelect.value = validOptions[0].value;
          this.variantSelect.hidden = true;
          this.variantSelect.classList.add("is-single-variant");
        } else if (
          this.selectedVariantDefault &&
          !window.location.search.includes("?variant=")
        ) {
          this.variantSelect.value = "";
        }
      }

      init() {
        if (!this.mainAddToCart) {
          this.container.style.setProperty("--m-translate-y", 0);
          return;
        }

        const { prodTitle, mainImage, addToCart, buyNowBtn, foxkitBtn } =
          this.domNodes;
        if (buyNowBtn) this.enable_dynamic_checkout = true;

        const rootMargin = `${MinimogSettings.headerHeight || 66}px 0px 0px 0px`;
        if ("IntersectionObserver" in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const showStickyCart = entry.intersectionRatio !== 1;
                this.container.style.setProperty(
                  "--m-translate-y",
                  showStickyCart ? 0 : "100%",
                );
                document.documentElement.classList.toggle(
                  "stick-atc-show",
                  showStickyCart,
                );
                this.setStickyAddToCartHeight();
              });
            },
            { threshold: 1, rootMargin },
          );
        }

        prodTitle?.addEventListener("click", () => __scrollToTop());
        mainImage?.addEventListener("click", () => __scrollToTop());

        if (this.hasCustomFields) {
          let hasCustomFieldRequired = false;
          const customFields = document.querySelectorAll(
            ".m-main-product--info .m-product-custom-field",
          );
          customFields.forEach((item) => {
            const field = item.querySelector(".form-field");
            if (field?.value === "" && field.hasAttribute("required")) {
              hasCustomFieldRequired = true;
            }
          });

          if (hasCustomFieldRequired) {
            addToCart?.addEventListener("click", (event) => {
              event.preventDefault();
              event.stopPropagation();
              __scrollToTop(this.mainProductForm, () =>
                this.mainAddToCart.click(),
              );
            });
          }

          buyNowBtn?.addEventListener(
            "click",
            (event) => {
              if (validateForm(this.mainProductInfo).length > 0) {
                event.preventDefault();
                event.stopPropagation();
                __scrollToTop(this.mainProductForm, () =>
                  this.mainDynamicCheckout.click(),
                );
              }
            },
            true,
          );

          foxkitBtn?.addEventListener("click", (event) => {
            event.preventDefault();
            __scrollToTop(this.mainProductForm, () =>
              this.mainAddToCart.click(),
            );
          });
        }

        this.setObserveTarget();
        this.syncWithMainProductForm();
      }

      setObserveTarget() {
        if (this.observer && this.mainProductForm) {
          this.observer.observe(this.mainProductForm);
          this.observeTarget = this.mainProductForm;
        }
      }

      updateFromSelection() {
        const selectedVariantId = this.variantSelect?.value;
        this.currentVariant = this.variantSelect
          ? this.variantData.find(
              (variant) => variant.id === Number(selectedVariantId),
            )
          : this.variantData[0] || null;

        const isValid = this.isValidVariant(this.currentVariant);
        const isAvailable = isValid && this.currentVariant.available;

        this.container.classList.toggle("has-invalid-variant", !isValid);

        if (!isValid) {
          this.updateButton(true, "Select options");
        } else if (!isAvailable) {
          this.updateButton(true, window.MinimogStrings.soldOut);
        } else {
          this.updateButton(false, window.MinimogStrings.addToCart);
        }

        this.updateDisplayedPrice(this.currentVariant);
      }

      updateDisplayedPrice(variant) {
        const titleNode = this.querySelector(this.selectors.prodTitle);
        if (!titleNode) return;

        // Single-variant products already render their complete price block.
        if (titleNode.querySelector(".m-price")) return;

        let priceNode = titleNode.querySelector(
          ".m-sticky-addtocart--current-price",
        );
        if (!priceNode) {
          priceNode = document.createElement("span");
          priceNode.className = "m-sticky-addtocart--current-price";
          priceNode.setAttribute("aria-live", "polite");
          titleNode.appendChild(priceNode);
        }

        if (!variant) {
          const firstValidOption = Array.from(
            this.variantSelect?.options || [],
          ).find((option) => option.value && !option.disabled);
          const firstValidText = firstValidOption?.textContent || "";
          const firstPrice = firstValidText.includes(" - ")
            ? firstValidText.split(" - ").pop().trim()
            : "";
          priceNode.textContent = firstPrice ? `From ${firstPrice}` : "";
          return;
        }

        if (window.Shopify?.formatMoney) {
          priceNode.textContent = window.Shopify.formatMoney(
            variant.price,
            window.MinimogSettings?.money_format,
          );
          return;
        }

        const selectedOption = this.variantSelect?.selectedOptions?.[0];
        const selectedText = selectedOption?.textContent || "";
        const priceText = selectedText.includes(" - ")
          ? selectedText.split(" - ").pop().trim()
          : "";
        priceNode.textContent = priceText;
      }

      focusVariantSelect() {
        if (!this.variantSelect || this.variantSelect.hidden) return;
        this.variantSelect.setCustomValidity("Please select your product options.");
        this.variantSelect.reportValidity();
        this.variantSelect.focus();
        window.setTimeout(() => this.variantSelect.setCustomValidity(""), 0);
      }

      updateButton(disable = true, text) {
        const productForm = this.querySelector(".sticky-atc-form");
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector(
          '[name="add"] > .m-add-to-cart--text',
        );
        if (!addButton) return;

        this.buttonState = { disable, text };

        if (disable) {
          if (!addButton.hasAttribute("disabled")) {
            addButton.setAttribute("disabled", "disabled");
          }
          if (!addButton.classList.contains("disabled")) {
            addButton.classList.add("disabled");
          }
        } else {
          if (addButton.hasAttribute("disabled")) {
            addButton.removeAttribute("disabled");
          }
          if (addButton.classList.contains("disabled")) {
            addButton.classList.remove("disabled");
          }
        }

        const ariaDisabled = String(disable);
        if (addButton.getAttribute("aria-disabled") !== ariaDisabled) {
          addButton.setAttribute("aria-disabled", ariaDisabled);
        }
        if (addButtonText && text && addButtonText.textContent !== text) {
          addButtonText.textContent = text;
        }
      }

      observeStickyButton() {
        const addButton = this.querySelector('.sticky-atc-form [name="add"]');
        if (!addButton) return;

        this.buttonObserver = new MutationObserver(() => {
          if (!this.buttonState) return;
          this.updateButton(
            this.buttonState.disable,
            this.buttonState.text,
          );
        });
        this.buttonObserver.observe(addButton, {
          attributes: true,
          attributeFilter: ["disabled", "class", "aria-disabled"],
          childList: true,
          characterData: true,
          subtree: true,
        });
      }

      syncWithMainProductForm() {
        if (!window.MinimogEvents?.subscribe) return;

        window.MinimogEvents.subscribe(
          `${this.productId}__VARIANT_CHANGE`,
          async (variant) => {
            if (!this.variantSelect) return;

            if (!variant || this.isPlaceholderVariant(variant)) {
              this.variantSelect.value = "";
            } else {
              this.variantSelect.value = String(variant.id);
            }
            this.updateFromSelection();
          },
        );
      }

      setStickyAddToCartHeight() {
        const stickyHeight = this.container?.offsetHeight || this.offsetHeight;
        const height = `${stickyHeight}px`;
        document.documentElement.style.setProperty(
          "--f-sticky-atc-bar-height",
          height,
        );
        window.MinimogSettings.stickyAddToCartHeight = stickyHeight;
      }
    },
  );



/* FBT uses Code Black Belt's native DOM; product-page styles load before the app markup. */
